// src/services/wallet.js
import { ethers } from 'ethers';

// Variable global para almacenar la wallet del usuario
let userWallet = null;

/**
 * Conecta con la wallet del usuario usando MetaMask o Abstract
 * @returns {Promise<string>} Dirección de la wallet
 */
async function connectWallet() {
  if (typeof window.ethereum !== "undefined") {
    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      userWallet = accounts[0];

      // Actualizar UI con la información de la wallet
      const connectBtn = document.getElementById('connectWalletBtn');
      const walletDisplay = document.createElement('div');
      walletDisplay.className = 'wallet-connected';
      walletDisplay.textContent = `🔗 ${userWallet.slice(0, 6)}...${userWallet.slice(-4)}`;
      
      connectBtn.parentNode.replaceChild(walletDisplay, connectBtn);

      // Log en la consola
      window.logToConsole(`🔗 Wallet connected: ${userWallet}`);

      // Cargar workers guardados
      const workers = window.loadWorkersForUser(userWallet);
      if (workers.length > 0) {
        window.logToConsole("✅ AI workers loaded");
        workers.forEach(w => window.mintRestoredCharacter(w));
      }
      
      return userWallet;
    } catch (err) {
      console.error("Connection failed", err);
      window.logToConsole("❌ Failed to connect wallet");
      return null;
    }
  } else {
    alert("Please install MetaMask or Abstract Wallet!");
    return null;
  }
}

/**
 * Obtiene información sobre los tokens en la wallet especificada
 * @param {string} wallet - Dirección de la wallet
 * @returns {Promise<string[]>} Array con los balances formateados
 */
async function getWalletInfo(wallet) {
  try {
    // Si no hay provider, devolver datos de prueba
    if (!window.ethereum) {
      return [
        "1.23 BIG", 
        "45.67 USDC", 
        "0.89 DAI"
      ];
    }
    
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    
    // Lista de tokens ERC20 para consultar
    // En producción, estas serían direcciones reales
    const erc20List = [
      { name: "BIG", address: "0xTuTokenBIG", decimals: 18 },
      { name: "USDC", address: "0xTuTokenUSDC", decimals: 6 },
      { name: "DAI", address: "0xTuTokenDAI", decimals: 18 }
    ];

    const balances = [];
    for (const token of erc20List) {
      try {
        const contract = new ethers.Contract(
          token.address,
          ["function balanceOf(address) view returns (uint256)"],
          provider
        );
        const rawBalance = await contract.balanceOf(wallet);
        const formatted = parseFloat(ethers.utils.formatUnits(rawBalance, token.decimals)).toFixed(2);
        balances.push(`${formatted} ${token.name}`);
      } catch (err) {
        // Si falla un token, añadir un valor de prueba
        console.warn(`Error fetching ${token.name} balance:`, err);
        balances.push(`0.00 ${token.name}`);
      }
    }

    return balances;
  } catch (err) {
    console.error("Error fetching token balances:", err);
    return ["0 tokens found."];
  }
}

/**
 * Genera una dirección de wallet aleatoria para uso de demostración
 * @returns {string} Dirección de wallet generada
 */
function generateMockWalletAddress() {
  return "0x" + [...crypto.getRandomValues(new Uint8Array(20))]
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Guarda la información de los workers en localStorage
 * @param {string} pubkey - Llave pública de la wallet
 * @param {Array} workers - Array de workers para guardar
 */
function saveWorkersForUser(pubkey, workers) {
  localStorage.setItem(`workers_${pubkey}`, JSON.stringify(workers));
}

/**
 * Carga la información de los workers desde localStorage
 * @param {string} pubkey - Llave pública de la wallet
 * @returns {Array} Array de workers guardados
 */
function loadWorkersForUser(pubkey) {
  const data = localStorage.getItem(`workers_${pubkey}`);
  return data ? JSON.parse(data) : [];
}

// Exportaciones
export {
  connectWallet,
  getWalletInfo,
  generateMockWalletAddress,
  saveWorkersForUser,
  loadWorkersForUser,
  userWallet
};